import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, throwError, timeout, retry } from 'rxjs';
import { environment } from '../../../environment';
import {
  LoginDTO,
  AuthResponseDTO,
  RefreshTokenDTO,
  LogoutDTO,
  ChangePasswordDTO,
  ApiResponse,
  UpdateProfileDTO,
  UserDTO,
  UserSessionDTO,
} from '../../../types/user-profile.types';

@Injectable({
  providedIn: 'root',
})
export class UserProfileApiService {
  private readonly baseUrl = environment.authApiUrl;
  private readonly defaultTimeout = 10000; // 10 seconds
  private readonly retryAttempts = 2;

  constructor(private http: HttpClient) {}

  /**
   * Login a user
   * @param data - LoginDTO
   * @returns Observable<ApiResponse<AuthResponseDTO>>
   */
  login(data: LoginDTO): Observable<ApiResponse<AuthResponseDTO>> {
    return this.http
      .post<ApiResponse<AuthResponseDTO>>(`${this.baseUrl}/auth/login`, data, {
        headers: this.getHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Refresh token
   * @param data - RefreshTokenDTO
   * @returns Observable<ApiResponse<AuthResponseDTO>>
   */
  refreshToken(
    data: RefreshTokenDTO
  ): Observable<ApiResponse<AuthResponseDTO>> {
    return this.http
      .post<ApiResponse<AuthResponseDTO>>(
        `${this.baseUrl}/auth/refresh`,
        { refresh_token: data.refresh_token },
        {
          headers: this.getHeaders(),
        }
      )
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Logout
   * @param data - LogoutDTO
   * @returns Observable<ApiResponse<null>>
   */
  logout(data: LogoutDTO): Observable<ApiResponse<null>> {
    return this.http
      .post<ApiResponse<null>>(`${this.baseUrl}/auth/logout`, data, {
        headers: this.getAuthHeaders(),
      })
      .pipe(timeout(this.defaultTimeout), catchError(this.handleError));
  }

  /**
   * Change password
   * @param data - ChangePasswordDTO
   * @returns Observable<ApiResponse<null>>
   */
  changePassword(data: ChangePasswordDTO): Observable<ApiResponse<null>> {
    return this.http
      .put<ApiResponse<null>>(`${this.baseUrl}/users/change-password`, data, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Get user profile
   * @returns Observable<ApiResponse<UserDTO>>
   */
  getProfile(): Observable<ApiResponse<UserDTO>> {
    return this.http
      .get<ApiResponse<UserDTO>>(`${this.baseUrl}/users/profile`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Update user profile
   * @param data - UpdateProfileDTO
   * @returns Observable<ApiResponse<null>>
   */
  updateProfile(data: UpdateProfileDTO): Observable<ApiResponse<null>> {
    return this.http
      .put<ApiResponse<null>>(`${this.baseUrl}/users/profile`, data, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Get user sessions
   * @returns Observable<ApiResponse<UserSessionDTO[]>>
   */
  getUserSessions(): Observable<ApiResponse<UserSessionDTO[]>> {
    return this.http
      .get<ApiResponse<UserSessionDTO[]>>(`${this.baseUrl}/user/session`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Invalidate a user session
   * @param sessionId - string
   * @returns Observable<ApiResponse<null>>
   */
  invalidateSession(sessionId: string): Observable<ApiResponse<null>> {
    return this.http
      .delete<ApiResponse<null>>(
        `${this.baseUrl}/user/session/${sessionId}`,
        {
          headers: this.getAuthHeaders(true),
        }
      )
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Get standard headers
   * @returns HttpHeaders
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  /**
   * Get authenticated headers
   * @param includeSessionId - boolean
   * @returns HttpHeaders
   */
  private getAuthHeaders(includeSessionId = false): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    if (includeSessionId) {
      const sessionId = localStorage.getItem('session_id');
      if (sessionId) {
        headers = headers.set('X-Session-ID', sessionId);
      }
    }

    return headers;
  }

  /**
   * Handle HTTP errors
   * @param error - any
   * @returns Observable<never>
   */
  private handleError = (error: any): Observable<never> => {
    let errorMessage = 'An unknown error occurred';

    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    console.error('UserProfileApiService Error:', error);
    return throwError(() => new Error(errorMessage));
  };
}
